import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, FileEdit, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/company";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};
const num = (n) => Number(n || 0);

const PackingListViewPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => {
    apiClient.get(`/packing-lists/${id}`)
      .then(({ data }) => setP(data))
      .catch(() => { toast.error("Packing list not found"); nav("/packing-lists"); });
  }, [id, nav]);

  if (!p) return <div className="p-8 text-sm text-slate-400">Loading…</div>;

  const items = p.items || [];
  const totalQty = items.reduce((s, it) => s + num(it.quantity), 0);
  const totalNet = items.reduce((s, it) => s + num(it.quantity) * num(it.weight_per_unit), 0);
  const unitLabel = items.find((it) => it.unit)?.unit || "Nos";

  // Filename-safe: convert slashes to dashes (PL numbers contain "/"), strip other illegal chars.
  const sanitize = (s) => (s || "")
    .replace(/[/\\]+/g, "-")
    .replace(/[?%*:|"<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const handlePrint = () => {
    const filename = `${sanitize(p.customer_name)} - ${sanitize(p.pl_ref)}`.trim();
    const original = document.title;
    document.title = filename || original;
    setTimeout(() => {
      window.print();
      setTimeout(() => { document.title = original; }, 1000);
    }, 50);
  };

  return (
    <div className="bg-slate-100 p-0">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-[#E5E7EB] bg-white px-6 py-3">
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => nav("/packing-lists")} data-testid="back-to-packing-lists">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-sm" data-testid="edit-packing-list-button">
            <Link to={`/packing-lists/${p.id}/edit`}>
              <FileEdit className="mr-2 h-4 w-4" /> Edit
            </Link>
          </Button>
          <Button size="sm" className="rounded-sm ep-amber hover:ep-amber" onClick={handlePrint} data-testid="print-packing-list-button">
            <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      <div className="mx-auto my-6 bg-white shadow-md pdf-page" data-testid="packing-list-preview">
        {/* Letterhead */}
        <div className="flex items-start justify-between gap-6 border-b-2 border-[#1E40AF] pb-4">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logoSrc} alt="Excellent Plastic" className="h-16 w-16 object-contain" />
            <div>
              <div className="font-heading text-2xl font-extrabold uppercase leading-none text-[#1E40AF]">
                Excellent Plastic
              </div>
              <div className="mt-1 text-[10px] leading-tight text-slate-500">
                Materials Trading LLC<br />
                14th St – Al Qusais Industrial Area, Dubai – UAE
              </div>
            </div>
          </div>
          <div className="text-right text-[10px] text-slate-500">
            <div>{COMPANY.phone}</div>
            <div>{COMPANY.mobile}</div>
            <div>{COMPANY.email}</div>
            <div>{COMPANY.website}</div>
          </div>
        </div>

        {/* Title + PL meta */}
        <div className="mt-5 flex items-start justify-between">
          <div className="font-heading text-2xl font-bold uppercase tracking-[0.2em] text-[#1E40AF]">
            Packing List
          </div>
          <div className="text-sm">
            <div className="flex justify-end gap-2">
              <span className="font-semibold uppercase text-slate-500">PL No:</span>
              <span className="font-bold text-slate-800" data-testid="pl-ref">{p.pl_ref}</span>
            </div>
            <div className="flex justify-end gap-2">
              <span className="font-semibold uppercase text-slate-500">Date:</span>
              <span className="text-slate-800">{formatDate(p.pl_date)}</span>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="mt-5 text-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bill To:</div>
          <div className="mt-1 font-bold text-slate-800">M/S, {p.customer_name}</div>
          {p.customer_place && <div className="text-slate-600">{p.customer_place}</div>}
          {p.customer_details && <div className="whitespace-pre-wrap text-slate-600">{p.customer_details}</div>}
          {p.customer_contact && <div className="text-slate-600">Tel: {p.customer_contact}</div>}
          {p.customer_trn && <div className="text-slate-600">TRN: {p.customer_trn}</div>}
        </div>

        {/* Items table */}
        <div className="mt-5 border border-[#E5E7EB]" data-testid="packing-items-table">
          <div className="grid grid-cols-12 bg-[#1E40AF] text-white">
            <div className="col-span-1 border-r border-[#1E40AF] px-2 py-2 text-left text-xs font-semibold uppercase">SI No</div>
            <div className="col-span-5 border-r border-[#1E40AF] px-2 py-2 text-left text-xs font-semibold uppercase">Item Description</div>
            <div className="col-span-2 border-r border-[#1E40AF] px-2 py-2 text-right text-xs font-semibold uppercase">Quantity</div>
            <div className="col-span-2 border-r border-[#1E40AF] px-2 py-2 text-right text-xs font-semibold uppercase">Weight/Units</div>
            <div className="col-span-2 px-2 py-2 text-right text-xs font-semibold uppercase">Net Weight</div>
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 border-t border-[#E5E7EB]" data-testid={`packing-item-${i}`}>
              <div className="col-span-1 border-r border-[#E5E7EB] px-2 py-2 text-sm text-slate-500">{i + 1}</div>
              <div className="col-span-5 border-r border-[#E5E7EB] px-2 py-2 text-sm font-medium text-slate-800 whitespace-pre-wrap">{it.description}</div>
              <div className="col-span-2 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">{num(it.quantity)} {it.unit || "Nos"}</div>
              <div className="col-span-2 border-r border-[#E5E7EB] px-2 py-2 text-right text-sm tabular-nums">{num(it.weight_per_unit)} Kg</div>
              <div className="col-span-2 px-2 py-2 text-right text-sm font-semibold tabular-nums">{(num(it.quantity) * num(it.weight_per_unit)).toFixed(2)} Kg</div>
            </div>
          ))}
        </div>

        {/* Summary block */}
        <div className="mt-4 ml-auto w-full max-w-sm border border-[#E5E7EB] text-sm" data-testid="packing-summary">
          <SummaryRow label="Gross Weight" value={p.gross_weight || `${totalNet.toFixed(2)} Kg`} />
          <SummaryRow label="Total Quantity" value={`${totalQty} ${unitLabel}`} />
          <SummaryRow label="Net Weight" value={`${totalNet.toFixed(2)} Kg`} />
          <SummaryRow label="HS Code" value={p.hs_code || "—"} />
          <SummaryRow label="Packing" value={p.packing || "—"} />
          {p.cbm ? <SummaryRow label="CBM" value={p.cbm} /> : null}
        </div>

        {p.notes && (
          <div className="mt-4 text-sm">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</div>
            <div className="mt-1 whitespace-pre-wrap text-slate-700">{p.notes}</div>
          </div>
        )}

        {/* Signature */}
        <div className="mt-10 text-sm">
          <div className="text-slate-700">Your Faithfully,</div>
          <img
            src={COMPANY.sealSrc}
            alt="Company Seal"
            className="mt-2 h-24 w-auto object-contain"
            data-testid="packing-list-seal"
          />
          <div className="mt-1 font-semibold text-[#1E40AF]">Excellent Plastic Materials Trading LLC</div>
          {p.owner_name && <div className="text-xs text-slate-600">{p.owner_name}{p.owner_designation ? ` – ${p.owner_designation}` : ""}</div>}
        </div>

        {/* Footer band */}
        <div className="mt-8 border-t border-[#E5E7EB] pt-3 text-center text-[9px] uppercase tracking-[0.2em] text-slate-400">
          This is a computer generated packing list.
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-[#E5E7EB] px-3 py-1.5 last:border-b-0">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
    <span className="tabular-nums font-medium text-slate-800">{value}</span>
  </div>
);

export default PackingListViewPage;
